import React from 'react';
import {
  View,
  Text,
  TextInput,
  ScrollView,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { METRICS_CONFIG, MetricConfig } from '../types';
import { styles } from '../PeopleHubConfiguration.styles';

interface WeeklyTargetGlidepathCardProps {
  onboardingWeeks: number;
  milestoneWeeks: [number, number, number];
  targets: Record<string, number[]>;
  onTargetCellChange: (metricKey: string, weekIndex: number, val: string) => void;
  onReset: () => void;
  onSave: () => void;
  hasChanges: boolean;
  canSave: boolean;
  saveLoading?: boolean;
  /** Filtered metric list for the current org/dept. Defaults to all five metrics. */
  metrics?: MetricConfig[];
}

export const WeeklyTargetGlidepathCard: React.FC<WeeklyTargetGlidepathCardProps> = ({
  onboardingWeeks,
  milestoneWeeks,
  targets,
  onTargetCellChange,
  onReset,
  onSave,
  hasChanges,
  canSave,
  saveLoading = false,
  metrics,
}) => {
  const activeMetrics = metrics ?? METRICS_CONFIG;
  const getMilestoneIndex = (weekNumber: number): number => {
    return milestoneWeeks.indexOf(weekNumber);
  };

  return (
    <View style={styles.card}>
      {/* Header */}
      <View style={[styles.cardHeader, { paddingHorizontal: 22, paddingTop: 22, paddingBottom: 14 }]}>
        <View style={styles.cardTitleGroup}>
          <Text style={styles.cardTitle}>Weekly Pulse Check Target Glidepath</Text>
          <Text style={styles.cardSubtitle}>
            Targets are compared with the employee’s metric result for each onboarding week.
          </Text>
        </View>
        <View style={styles.badgeWeeksCount}>
          <Text style={styles.badgeWeeksCountText}>{onboardingWeeks} weeks</Text>
        </View>
      </View>

      {/* Table Container */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={true}
        style={styles.tableScrollWrap}
        contentContainerStyle={styles.tableScrollContent}
      >
        <View style={styles.table}>
          {/* Table Header Row (Solid Navy Blue) */}
          <View style={styles.tableHeaderRow}>
            <View style={styles.thCellWeek}>
              <Text style={[styles.thText, { textAlign: 'left' }]}>Week</Text>
            </View>
            {activeMetrics.map((metric) => (
              <View key={metric.key} style={styles.thCellMetric}>
                <Text style={styles.thText}>{metric.label}</Text>
                <Text style={styles.thSubText}>{metric.unit}</Text>
              </View>
            ))}
          </View>

          {/* Table Body (1 Row per Week) */}
          {Array.from({ length: onboardingWeeks }, (_, weekIdx) => {
            const weekNumber = weekIdx + 1;
            const milestoneIdx = getMilestoneIndex(weekNumber);
            const isMilestone = milestoneIdx !== -1;

            return (
              <View key={`week-${weekNumber}`} style={styles.tableRow}>
                {/* Week Label Column */}
                <View style={styles.tdCellWeek}>
                  <Text style={styles.tdWeekLabel}>Week {weekNumber}</Text>
                  {isMilestone && (
                    <View style={styles.milestoneTag}>
                      <Text style={styles.milestoneTagText}>
                        Milestone {milestoneIdx + 1}
                      </Text>
                    </View>
                  )}
                </View>

                {/* 5 Metric Target Input Cells */}
                {activeMetrics.map((metric) => {
                  const metricValues = targets[metric.key] || [];
                  const cellValue = metricValues[weekIdx] !== undefined ? String(metricValues[weekIdx]) : '';

                  return (
                    <View key={`${metric.key}-${weekNumber}`} style={styles.tdCellMetric}>
                      <TextInput
                        style={styles.metricInput}
                        keyboardType="decimal-pad"
                        value={cellValue}
                        onChangeText={(val) => onTargetCellChange(metric.key, weekIdx, val)}
                        selectTextOnFocus
                        accessibilityLabel={`${metric.label} week ${weekNumber}`}
                      />
                    </View>
                  );
                })}
              </View>
            );
          })}
        </View>
      </ScrollView>

      {/* Footer Actions (Reset Changes & Save Configuration) */}
      <View style={styles.tableFooterActions}>
        <TouchableOpacity
          style={[styles.btnSecondary, !hasChanges && { opacity: 0.5 }]}
          onPress={onReset}
          disabled={!hasChanges}
          activeOpacity={0.8}
        >
          <Text style={styles.btnSecondaryText}>Reset Changes</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.btnPrimary, !canSave && styles.btnPrimaryDisabled]}
          onPress={onSave}
          disabled={!canSave || saveLoading}
          activeOpacity={0.8}
        >
          <Text style={styles.btnPrimaryText}>
            {saveLoading ? 'Saving…' : 'Save Configuration'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};
